import { useCallback, useEffect, useRef, useState } from "react";
import { CAROUSEL_FALLBACK_DWELL_MS } from "@/content";
import { usePlaybackToggle } from "./usePlaybackToggle";

const VISIBILITY_THRESHOLD = 0.25;

const DWELL_TAIL_MS = 400;

type UseStepCarouselOptions = {
  count: number;
  index: number;
  onAdvance: (next: number) => void;
};

export const useStepCarousel = ({ count, index, onAdvance }: UseStepCarouselOptions) => {
  const { isPlaying: wantsPlayback, toggle: togglePlayback } = usePlaybackToggle();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const onAdvanceRef = useRef(onAdvance);

  const [isAuto, setIsAuto] = useState(true);
  const [isOnScreen, setIsOnScreen] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [metadataToken, setMetadataToken] = useState(0);
  const [progress, setProgress] = useState(0);

  const isPlaying = wantsPlayback && isOnScreen && isPageVisible;
  const autoAdvances = isAuto && isPlaying;

  useEffect(() => {
    onAdvanceRef.current = onAdvance;
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(([entry]) => setIsOnScreen(entry.isIntersecting), {
      threshold: VISIBILITY_THRESHOLD,
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleVisibility = () => setIsPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.currentTime = 0;
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [index, isPlaying]);

  useEffect(() => {
    setProgress(0);
    if (!autoAdvances) return;

    const video = videoRef.current;
    const clipMs =
      video && Number.isFinite(video.duration) && video.duration > 0 ? Math.round(video.duration * 1000) : 0;
    const dwell = clipMs ? clipMs + DWELL_TAIL_MS : CAROUSEL_FALLBACK_DWELL_MS;

    const start = performance.now();
    let frame = requestAnimationFrame(function paint(now: number) {
      const elapsed = Math.min((now - start) / dwell, 1);
      setProgress(elapsed);
      if (elapsed < 1) frame = requestAnimationFrame(paint);
    });

    const timer = window.setTimeout(() => onAdvanceRef.current((index + 1) % count), dwell);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [index, count, autoAdvances, metadataToken]);

  const stop = useCallback(() => setIsAuto(false), []);

  const handleLoadedMetadata = useCallback(() => setMetadataToken((token) => token + 1), []);

  return {
    containerRef,
    videoRef,
    progress,
    autoAdvances,
    isPlaying: wantsPlayback,
    togglePlayback,
    stop,
    handleLoadedMetadata,
  };
};
